/* Wellness Whispers theme — vanilla JS, no build step. Features are opt-in via data attributes. */
(() => {
  'use strict';
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
  const url = (path) => `${root.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

  /* ── Toast ── */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ── Drawers (menu + cart) ── */
  let lastFocus = null;
  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    document.body.classList.add('no-scroll');
    $$(`[aria-controls="${drawer.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => $('[data-drawer-close]:not(.drawer__overlay)', drawer)?.focus());
  }
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (!$('.drawer.is-open')) document.body.classList.remove('no-scroll');
    $$(`[aria-controls="${drawer.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    lastFocus?.focus?.();
  }
  function initDrawers() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) { e.preventDefault(); openDrawer(document.getElementById(opener.dataset.drawerOpen)); return; }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) { closeDrawer(closer.closest('[data-drawer]')); return; }
      const cartLink = e.target.closest('[data-cart-open]');
      if (cartLink && window.theme?.cartDrawer && $('#cart-drawer') && document.body.dataset.page !== 'cart') {
        e.preventDefault(); openDrawer($('#cart-drawer'));
      }
    });
    document.addEventListener('keydown', (e) => {
      const open = $('.drawer.is-open');
      if (!open) return;
      if (e.key === 'Escape') return closeDrawer(open);
      if (e.key !== 'Tab') return;
      const items = $$('a[href], button:not([disabled]), input, select', $('.drawer__panel', open)).filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ── Cart (AJAX API + Section Rendering) ── */
  function setCount(count) {
    $$('[data-cart-count]').forEach(el => { el.textContent = count; el.hidden = count === 0; });
  }
  function renderDrawer(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-inner]', doc);
    const current = $('#cart-drawer [data-cart-inner]');
    if (fresh && current) current.replaceWith(fresh);
  }
  async function cartRequest(path, body) {
    const res = await fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }
  async function refreshAfter(data) {
    if (data.sections) renderDrawer(data.sections['cart-drawer']);
    const cart = data.item_count !== undefined && data.items ? data : await fetch(url('cart.js')).then(r => r.json());
    setCount(cart.item_count);
  }
  async function addToCart(items, button) {
    const label = button?.innerHTML;
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    try {
      const data = await cartRequest('cart/add.js', { items });
      await refreshAfter(data);
      if (window.theme?.cartDrawer && $('#cart-drawer')) openDrawer($('#cart-drawer'));
      else toast('Added to your bag');
    } catch (err) {
      toast(err.message);
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); button.innerHTML = label; }
    }
  }
  async function changeLine(key, quantity) {
    const drawer = $('#cart-drawer [data-cart-inner]');
    drawer?.setAttribute('aria-busy', 'true');
    drawer && (drawer.style.opacity = '.6');
    try {
      const data = await cartRequest('cart/change.js', { id: key, quantity: Math.max(0, quantity) });
      await refreshAfter(data);
    } catch (err) { toast(err.message); }
    finally { const d = $('#cart-drawer [data-cart-inner]'); d && (d.style.opacity = ''); d?.removeAttribute('aria-busy'); }
  }
  function initCart() {
    document.addEventListener('click', (e) => {
      const quick = e.target.closest('[data-quick-add]');
      if (quick) { e.preventDefault(); addToCart([{ id: Number(quick.dataset.quickAdd), quantity: 1 }], quick); return; }
      const line = e.target.closest('[data-line-change]');
      if (line) { e.preventDefault(); changeLine(line.dataset.lineChange, Number(line.dataset.qty)); }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-input]');
      if (input) changeLine(input.dataset.lineInput, Number(input.value));
    });
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const fd = new FormData(form);
      addToCart([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }], $('[data-atc]', form));
    });
  }

  /* ── "RSVP" / "Apply" buttons pre-select the matching option in the page form ── */
  function initPrefill() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-prefill]');
      if (!btn) return;
      const select = document.getElementById(btn.dataset.prefill);
      if (!select) return;
      const opt = Array.from(select.options).find(o => o.value === btn.dataset.value || o.text === btn.dataset.value);
      if (opt) select.value = opt.value;
      setTimeout(() => select.focus({ preventScroll: true }), 400);
    });
  }

  /* ── Reviews: "Show all" and reopening the form after a submission ── */
  function initReviews() {
    document.addEventListener('click', (e) => {
      const more = e.target.closest('[data-reviews-more]');
      if (!more) return;
      $$('.review--more').forEach(r => { r.hidden = false; });
      more.remove();
    });
    const write = $('[data-review-write]');
    if (write && (write.querySelector('.notice') || /contact_posted=true/.test(location.search) && location.hash.includes('review'))) {
      write.open = true;
      write.scrollIntoView({ block: 'center' });
    }
  }

  /* ── Variant option pills (size, color…): pick the matching variant ── */
  function initVariantPills() {
    const form = $('form[data-product-form]');
    const data = $('[data-variants]');
    if (!form || !data) return;
    let variants = [];
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    const idInput = $('[data-variant-id]', form);
    const atc = $('[data-atc]', form);
    const sets = $$('.option-picker', form);
    const fmt = (cents) => '₱' + (cents / 100).toLocaleString('en-PH', { maximumFractionDigits: 2 });
    const update = () => {
      const chosen = sets.map(set => set.querySelector('input:checked')?.value);
      sets.forEach((set, i) => { const l = $('[data-option-label]', set); if (l) l.textContent = chosen[i] || ''; });
      const v = variants.find(x => x.options.every((o, i) => o === chosen[i]));
      // Mark values that have no available variant with the other current choices
      sets.forEach((set, i) => $$('input', set).forEach(inp => {
        const test = chosen.slice(); test[i] = inp.value;
        const match = variants.find(x => x.options.every((o, k) => o === test[k]));
        inp.closest('.option-pill').classList.toggle('is-unavailable', !match || !match.available);
      }));
      if (!v) { if (atc) { atc.disabled = true; atc.textContent = 'Unavailable'; } return; }
      idInput.value = v.id;
      $$('[data-price]').forEach(el => { el.textContent = fmt(v.price); });
      if (atc) { atc.disabled = !v.available; atc.innerHTML = v.available ? atc.dataset.label || atc.innerHTML : 'Sold out'; }
      const u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState(null, '', u);
    };
    if (atc) atc.dataset.label = atc.innerHTML;
    sets.forEach(set => set.addEventListener('change', update));
    update();
  }

  /* ── Bundle kits: add several blends to the bag in one go ── */
  function initKits() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-kit]');
      if (!btn) return;
      e.preventDefault();
      let items = [];
      try { items = JSON.parse(btn.dataset.addKit); } catch (err) { return; }
      if (items.length) addToCart(items, btn);
    });
  }

  /* ── Product page: qty, variants, gallery, sticky bar ── */
  function initProduct() {
    $$('[data-qty]').forEach(q => {
      const input = $('[data-qty-input]', q);
      if (!input) return;
      $('[data-qty-minus]', q)?.addEventListener('click', () => { input.value = Math.max(1, (Number(input.value) || 1) - 1); });
      $('[data-qty-plus]', q)?.addEventListener('click', () => { input.value = Math.min(Number(input.max) || 99, (Number(input.value) || 1) + 1); });
      // Supply picker (Bundle & Save): radios set the quantity; typing a quantity selects the matching radio.
      const supply = $('[data-supply]');
      if (supply) {
        const radios = $$('input[name="supply"]', supply);
        const sync = () => { const v = String(Number(input.value) || 1); radios.forEach(r => { r.checked = r.value === v; }); };
        radios.forEach(r => r.addEventListener('change', () => { if (r.checked) input.value = r.value; }));
        ['input', 'change'].forEach(ev => input.addEventListener(ev, sync));
        $$('[data-qty-minus],[data-qty-plus]', q).forEach(b => b.addEventListener('click', () => setTimeout(sync, 0)));
      }
    });
    $$('[data-variant-select]').forEach(select => select.addEventListener('change', () => {
      const price = select.selectedOptions[0]?.dataset.price;
      if (price) $$('[data-price]').forEach(el => { el.textContent = price; });
    }));

    $$('[data-gallery]').forEach(g => {
      const track = $('[data-gallery-track]', g);
      if (!track) return;
      const slides = $$('.gallery__slide', track);
      const thumbs = $$('[data-gallery-thumb]', g);
      const dotsWrap = $('[data-gallery-dots]', g);
      const navs = $$('.gallery__nav', g);
      if (slides.length < 2) { navs.forEach(n => n.hidden = true); return; }
      const dots = slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'gallery__dot'; b.setAttribute('aria-label', `Show image ${i + 1}`);
        b.addEventListener('click', () => go(i));
        dotsWrap?.append(b);
        return b;
      });
      let index = 0;
      const mark = (i) => {
        index = i;
        dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      };
      const go = (i) => { const n = (i + slides.length) % slides.length; track.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' }); mark(n); };
      track.addEventListener('scroll', () => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index) mark(Math.max(0, Math.min(slides.length - 1, i)));
      }, { passive: true });
      $('[data-gallery-prev]', g)?.addEventListener('click', () => go(index - 1));
      $('[data-gallery-next]', g)?.addEventListener('click', () => go(index + 1));
      thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
      mark(0);
    });

    const bar = $('[data-sticky-atc]');
    const mainBtn = $('[data-atc]');
    if (bar && mainBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        $('[data-sticky-submit]', bar).tabIndex = show ? 0 : -1;
      }).observe(mainBtn);
      $('[data-sticky-submit]', bar)?.addEventListener('click', () => $('#product-form')?.requestSubmit());
    }
  }

  /* ── Collection: need filter pills (?need=tag), sort ── */
  function initCollection() {
    const wrap = $('[data-shop-filter]');
    const grid = $('[data-filter-grid]');
    if (!wrap || !grid) return;
    const pills = $$('[data-filter]', wrap);
    const items = $$('[data-filter-item]', grid);
    const count = $('[data-filter-count]', wrap);
    const empty = $('[data-filter-empty]');
    const apply = (tag) => {
      let shown = 0;
      items.forEach(item => {
        const ok = !tag || (item.dataset.tags || '').includes(`|${tag}|`);
        item.hidden = !ok;
        if (ok) { shown++; item.classList.add('is-visible'); }
      });
      pills.forEach(p => p.setAttribute('aria-pressed', String(p.dataset.filter === tag)));
      if (count) count.textContent = shown;
      if (empty) empty.hidden = shown !== 0;
    };
    pills.forEach(p => p.addEventListener('click', () => {
      apply(p.dataset.filter);
      const u = new URL(window.location.href);
      if (p.dataset.handle) u.searchParams.set('need', p.dataset.handle); else u.searchParams.delete('need');
      history.replaceState(null, '', u);
      p.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }));
    const need = new URLSearchParams(window.location.search).get('need');
    if (need) {
      const pill = pills.find(p => p.dataset.handle === need);
      if (pill) apply(pill.dataset.filter);
    }
    $('[data-sort]', wrap)?.addEventListener('change', (e) => {
      const u = new URL(window.location.href);
      u.searchParams.set('sort_by', e.target.value);
      window.location.href = u.toString();
    });
  }

  /* ── Find my blend quiz ── */
  function initQuiz() {
    const el = $('[data-quiz]');
    if (!el) return;
    let products = [], config = {};
    try { products = JSON.parse($('[data-quiz-products]').textContent); config = JSON.parse($('[data-quiz-config]').textContent); } catch { return; }
    const q = $('[data-quiz-question]', el), r = $('[data-quiz-result]', el);
    const opts = $('[data-quiz-options]', el), back = $('[data-quiz-back]', el);
    const bar = $('[data-quiz-bar]', el), stepLabel = $('[data-quiz-step-label]', el);
    let step = 0, goal = null;

    const swatch = (inner, tint) => { const s = document.createElement('span'); s.className = 'quiz-option__swatch'; if (tint) s.style.setProperty('--tint', tint); if (inner instanceof Node) s.append(inner); else s.textContent = inner || ''; return s; };
    const option = (label, sub, swatchEl, onPick) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'quiz-option';
      const t = document.createElement('div');
      const s = document.createElement('strong'); s.textContent = label; t.append(s);
      if (sub) { const sp = document.createElement('span'); sp.textContent = sub; t.append(sp); }
      b.append(swatchEl, t); b.addEventListener('click', onPick); return b;
    };
    const pic = (p, alt = '') => {
      const picture = document.createElement('picture'); picture.className = 'pouch';
      if (p.avif) { const src = document.createElement('source'); src.type = 'image/avif'; src.srcset = p.avif; picture.append(src); }
      const img = document.createElement('img'); img.src = p.image; img.alt = alt; img.loading = 'lazy'; img.decoding = 'async';
      picture.append(img); return picture;
    };
    const byTag = (tag) => products.filter(p => p.available && (p.tags || []).includes(tag));
    const top = () => window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    // Quiz analytics: GA4 (gtag from the Google & YouTube app) + Shopify customer events (for Meta/TikTok pixels later).
    const amount = (p) => parseFloat(String(p.price).replace(/[^\d.]/g, '')) || undefined;
    const track = (name, params = {}) => {
      try { window.gtag && window.gtag('event', name, params); } catch (e) {}
      try { window.Shopify?.analytics?.publish?.(name, params); } catch (e) {}
    };
    let quizStyle = null, quizStarted = false;

    function render() {
      q.hidden = false; r.hidden = true;
      back.classList.toggle('is-hidden', step === 0);
      stepLabel.textContent = `Step ${step + 1} of 2`;
      bar.style.width = step === 0 ? '33%' : '66%';
      $('[data-quiz-q]', el).textContent = step === 0 ? config.q1 : config.q2;
      $('[data-quiz-hint]', el).textContent = step === 0 ? config.q1hint : config.q2hint;
      opts.replaceChildren();
      if (step === 0) {
        config.goals.forEach(g => {
          const match = byTag(g.tag)[0];
          let inner = '';
          if (match?.image) inner = pic(match);
          const count = byTag(g.tag).length;
          if (!count) return;
          opts.append(option(g.label, g.sub, swatch(inner, g.color), () => {
            if (!quizStarted) { quizStarted = true; track('quiz_start'); }
            goal = g; step = 1;
            track('quiz_goal_selected', { quiz_goal: g.label });
            render(); top();
          }));
        });
      } else {
        const pool = byTag(goal.tag);
        config.styles.forEach(s => {
          const n = pool.filter(p => s.keywords.some(k => p.text.includes(k.trim()))).length;
          const sub = n ? `${s.sub ? s.sub + ' · ' : ''}${n} ${n === 1 ? 'match' : 'matches'}` : `${s.sub ? s.sub + ' · ' : ''}we'll suggest the closest`;
          const sample = pool.find(p => s.keywords.some(k => p.text.includes(k.trim())) && p.image);
          opts.append(option(s.label, sub, swatch(sample ? pic(sample) : s.emoji, sample ? sample.tint : null), () => result(s)));
        });
      }
    }
    function result(style) {
      const pool = byTag(goal.tag);
      let matches = pool.filter(p => style.keywords.some(k => p.text.includes(k.trim())));
      const exact = matches.length > 0;
      if (!exact) matches = pool;
      if (!matches.length) matches = products.filter(p => p.available);
      const best = matches[0];
      quizStyle = style;
      const base = { quiz_goal: goal.label, quiz_style: style.label };
      track('quiz_complete', { ...base, recommended_item: best.title, item_id: String(best.id), exact_match: exact ? 'yes' : 'no' });
      q.hidden = true; r.hidden = false;
      bar.style.width = '100%'; stepLabel.textContent = 'Your match'; back.classList.remove('is-hidden'); step = 2;
      const media = $('[data-r-media]', r); media.href = best.url; media.style.setProperty('--tint', best.tint); media.replaceChildren();
      if (best.image) media.append(pic(best, best.title));
      $('[data-r-need]', r).textContent = best.need;
      $('[data-r-title]', r).textContent = best.title;
      $('[data-r-tagline]', r).textContent = best.tagline;
      $('[data-r-price]', r).textContent = best.price;
      $('[data-r-link]', r).href = best.url;
      $('[data-r-note]', r).hidden = exact;
      const add = $('[data-r-add]', r);
      add.hidden = !best.simple;
      add.onclick = () => {
        track('quiz_add_to_cart', { ...base, recommended_item: best.title, item_id: String(best.id), value: amount(best), currency: 'PHP' });
        addToCart([{ id: best.id, quantity: 1 }], add);
      };
      const clickParams = (p, slot) => ({ ...base, recommended_item: p.title, item_id: String(p.id), result_slot: slot });
      media.onclick = () => track('quiz_result_click', clickParams(best, 'main'));
      $('[data-r-link]', r).onclick = () => track('quiz_result_click', clickParams(best, 'main'));
      const others = [...matches.slice(1), ...pool.filter(p => !matches.includes(p))].filter(p => p !== best).slice(0, 4);
      const moreWrap = $('[data-r-more-wrap]', r), more = $('[data-r-more]', r);
      moreWrap.hidden = others.length === 0;
      more.replaceChildren(...others.map(p => {
        const a = document.createElement('a'); a.href = p.url; a.className = 'card';
        a.addEventListener('click', () => track('quiz_result_click', clickParams(p, 'also_great')));
        a.innerHTML = '<span class="card__media"></span><span class="card__body"><span class="card__need"></span><span class="card__title"></span><span class="card__price"></span></span>';
        const m = $('.card__media', a); m.style.setProperty('--tint', p.tint);
        if (p.image) m.append(pic(p));
        $('.card__need', a).textContent = p.need; $('.card__title', a).textContent = p.title; $('.card__price', a).textContent = p.price;
        return a;
      }));
      top();
    }
    back.addEventListener('click', () => { step = step === 2 ? 1 : 0; render(); });
    $('[data-quiz-restart]', el).addEventListener('click', () => {
      track('quiz_restart', { quiz_goal: goal?.label, quiz_style: quizStyle?.label });
      step = 0; goal = null; quizStyle = null; render(); top();
    });
    render();
  }


  /* ── Homepage banner carousel ── */
  function initHeroSliders() {
    $$('[data-hero-slider]:not([data-ready])').forEach(root => {
      root.dataset.ready = '1';
      const track = $('[data-slider-track]', root);
      const slides = $$('[data-slide]', root);
      const dots = $$('[data-slider-dot]', root);
      const toggle = $('[data-slider-toggle]', root);
      if (!track || slides.length === 0) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = 0, timer = null, paused = root.dataset.autoplay !== 'true' || reduce, hover = false;
      const interval = Number(root.dataset.interval) || 6000;

      const syncMedia = () => slides.forEach((s, i) => {
        s.setAttribute('aria-hidden', String(i !== index));
        $$('a, button', s).forEach(el => { el.tabIndex = i === index ? 0 : -1; });
        $$('video', s).forEach(v => { if (i === index && v.offsetParent !== null) { v.muted = true; v.play?.().catch(() => {}); } else { v.pause?.(); } });
      });
      const mark = (i) => {
        index = i;
        dots.forEach((d, n) => { d.classList.toggle('is-active', n === i); d.setAttribute('aria-selected', String(n === i)); });
        syncMedia();
      };
      const go = (i, user = false) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
        mark(n);
        if (user) restart();
      };
      const stop = () => { clearInterval(timer); timer = null; };
      const start = () => { stop(); if (!paused && !hover && slides.length > 1 && !document.hidden) timer = setInterval(() => go(index + 1), interval); };
      const restart = () => start();
      const setPaused = (v) => {
        paused = v;
        if (toggle) {
          toggle.setAttribute('aria-label', v ? 'Play slideshow' : 'Pause slideshow');
          toggle.innerHTML = v ? '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 5v14l11-7L7 5Z"/></svg>' : '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M9 5v14M15 5v14"/></svg>';
        }
        start();
      };

      let scrollTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index) { mark(Math.max(0, Math.min(slides.length - 1, i))); restart(); }
        }, 80);
      }, { passive: true });
      $('[data-slider-prev]', root)?.addEventListener('click', () => go(index - 1, true));
      $('[data-slider-next]', root)?.addEventListener('click', () => go(index + 1, true));
      dots.forEach((d, i) => d.addEventListener('click', () => go(i, true)));
      toggle?.addEventListener('click', () => setPaused(!paused));
      root.addEventListener('mouseenter', () => { hover = true; stop(); });
      root.addEventListener('mouseleave', () => { hover = false; start(); });
      root.addEventListener('focusin', () => { hover = true; stop(); });
      root.addEventListener('focusout', () => { hover = false; start(); });
      root.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') go(index + 1, true); if (e.key === 'ArrowLeft') go(index - 1, true); });
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
      window.addEventListener('resize', () => track.scrollTo({ left: slides[index].offsetLeft }), { passive: true });
      document.addEventListener('shopify:block:select', (e) => {
        const i = slides.findIndex(s => s.contains(e.target) || s === e.target);
        if (i >= 0) { hover = true; stop(); go(i); }
      });
      document.addEventListener('shopify:block:deselect', () => { hover = false; start(); });
      mark(0);
      setPaused(paused);
    });
  }

  /* ── Popup banners ── */
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
    sget(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    sset(k, v) { try { sessionStorage.setItem(k, v); } catch {} },
  };
  function initPopups() {
    const popups = $$('[data-popup]:not([data-ready])');
    popups.forEach(el => {
      el.dataset.ready = '1';
      const key = `ww-popup-${el.dataset.popupId}`;
      const freq = el.dataset.frequency;
      const design = el.dataset.designMode === 'true';
      let opened = false, lastFocus = null;

      const seenRecently = () => {
        if (freq === 'always') return false;
        if (freq === 'session') return store.sget(key) === '1';
        const rec = store.get(key);
        if (!rec) return false;
        if (rec.converted) return true;
        if (freq === 'once') return true;
        return Date.now() - rec.t < Number(freq) * 86400000;
      };
      const remember = (converted = false) => {
        store.sset(key, '1');
        store.set(key, { t: Date.now(), converted });
      };
      const open = (force = false) => {
        if (opened && !force) return;
        if (!force && ($('.drawer.is-open') || $('[data-popup]:not([hidden])'))) return;
        opened = true;
        lastFocus = document.activeElement;
        el.hidden = false;
        el.querySelectorAll('video').forEach(v => { v.muted = true; v.play?.().catch(() => {}); });
        if (!el.classList.contains('popup--corner')) document.body.classList.add('no-scroll');
        requestAnimationFrame(() => $('.popup__close', el)?.focus());
        if (!design) remember(false);
      };
      const close = () => {
        el.hidden = true;
        el.querySelectorAll('video').forEach(v => v.pause?.());
        if (!$('.drawer.is-open')) document.body.classList.remove('no-scroll');
        lastFocus?.focus?.();
      };
      $$('[data-popup-close]', el).forEach(b => b.addEventListener('click', close));
      $$('[data-popup-convert]', el).forEach(a => a.addEventListener('click', () => remember(true)));
      $('[data-copy-code]', el)?.addEventListener('click', async (e) => {
        const code = e.currentTarget.dataset.copyCode;
        try { await navigator.clipboard.writeText(code); toast(`Code ${code} copied`); } catch { toast(`Your code: ${code}`); }
        remember(true);
      });
      $('form', el)?.addEventListener('submit', () => remember(true));
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') return close();
        if (e.key !== 'Tab' || el.classList.contains('popup--corner')) return;
        const items = $$('a[href], button:not([disabled]), input', $('.popup__panel', el)).filter(x => x.offsetParent !== null);
        if (!items.length) return;
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      // After a newsletter signup Shopify reloads the page with the form id in the URL hash — show the success message.
      if (window.location.hash === `#${el.dataset.formId}`) { open(true); remember(true); return; }
      if (design) return; // in the theme editor, popups open via section select only
      if (seenRecently()) return;

      const trigger = el.dataset.trigger;
      const delay = Math.max(0, Number(el.dataset.delay) || 0) * 1000;
      const isTouch = window.matchMedia('(hover: none)').matches;
      if (trigger === 'scroll') {
        const pct = Number(el.dataset.scroll) || 40;
        const onScroll = () => {
          const h = document.documentElement.scrollHeight - window.innerHeight;
          if (h > 0 && (window.scrollY / h) * 100 >= pct) { window.removeEventListener('scroll', onScroll); open(); }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else if (trigger === 'exit' && !isTouch) {
        const onLeave = (e) => { if (e.clientY <= 0) { document.removeEventListener('mouseout', onLeave); open(); } };
        setTimeout(() => document.addEventListener('mouseout', onLeave), 3000);
      } else {
        setTimeout(() => open(), delay || (trigger === 'exit' ? 20000 : 0));
      }
    });
  }
  document.addEventListener('shopify:section:select', (e) => {
    const el = $('[data-popup]', e.target);
    if (el) { el.hidden = false; }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const el = $('[data-popup]', e.target);
    if (el) { el.hidden = true; document.body.classList.remove('no-scroll'); }
  });

  /* ── Reveal on scroll ── */
  /* ── Hide header + tab bar while scrolling down, show again on scroll up ── */
  function initAutoHideNav() {
    let lastY = window.scrollY, ticking = false;
    const update = () => {
      const y = window.scrollY;
      if (document.body.classList.contains('no-scroll')) { lastY = y; ticking = false; return; }
      if (y < 80) document.body.classList.remove('nav-hidden');
      else if (y > lastY + 8) document.body.classList.add('nav-hidden');
      else if (y < lastY - 8) document.body.classList.remove('nav-hidden');
      if (Math.abs(y - lastY) > 8) lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    document.addEventListener('focusin', (e) => { if (e.target.closest('.section-header, .tabbar')) document.body.classList.remove('nav-hidden'); });
  }

  function initReveal() {
    const items = $$('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) { items.forEach(i => i.classList.add('is-visible')); return; }
    const io = new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -40px 0px' });
    items.forEach(i => io.observe(i));
  }

  function start() {
    document.documentElement.classList.remove('no-js');
    document.body.dataset.page = document.body.className.includes('template-cart') ? 'cart' : '';
    initDrawers(); initAutoHideNav(); initPrefill(); initReviews(); initVariantPills(); initCart(); initKits(); initProduct(); initCollection(); initQuiz(); initHeroSliders(); initPopups(); initReveal();
  }
  document.addEventListener('shopify:section:load', () => { initProduct(); initCollection(); initQuiz(); initHeroSliders(); initPopups(); initReveal(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
